/**
 * Decidir si hace falta seguir leyendo audit_log hacia atrás, y hasta dónde.
 *
 * Existe por un fallo real: una confirmación grande escribe **un evento `progress` por chunk** (1711
 * en la de 342.185 viajes), y la pantalla leía los 300 eventos más recientes. Los 300 huecos se
 * llenaron con los latidos de esa misma confirmación y su `intent` —el ÚNICO evento que lleva
 * `actor`, `criterios` y `resumen`— se quedó fuera de la ventana. Consecuencia en pantalla: ni el
 * email de quien confirmó, ni las cifras, y el tiempo restante congelado en "calculando…" porque
 * `nViajes` venía de ese `intent`. De paso, las confirmaciones anteriores desaparecían de la lista,
 * expulsadas por los latidos de la nueva.
 *
 * La lógica vive aquí y no en `auditLogFirestore.ts` para que se pueda probar: ese módulo importa el
 * SDK de cliente y no corre en tests.
 *
 * Se paginan los MISMOS índices que ya se usaban (`orderBy createdAt desc` + `startAfter`), sin
 * consulta nueva por `tipo` — eso pediría un índice compuesto, y desplegar índices con una
 * confirmación en vuelo es tocar infraestructura por un problema de pantalla.
 */

export interface DocAudit {
  id: string;
  data: Record<string, unknown>;
}

/**
 * Los eventos que llevan la cabecera de una confirmación: actor, criterios, resumen, cifras.
 *
 * `cancelacion` cuenta como cabecera (BB-1676) aunque sea flaca, y no por lo que trae sino por lo que
 * pasa si se pierde. Solo las cabeceras se cachean entre páginas, así que un evento que no lo sea solo
 * existe mientras esté dentro de los 300 documentos más recientes. Un fin de semana con una
 * confirmación grande escribe miles de `progress` y lo empuja fuera — y entonces la fila deja de saber
 * que se canceló: `clasificar` cae en la rama del outcome y pinta **"Fallido" en rojo** una decisión
 * deliberada. Hallazgo del payment-safety-reviewer.
 */
export function esCabecera(doc: DocAudit): boolean {
  const tipo = doc.data.tipo;
  return tipo === 'intent' || tipo === 'outcome' || tipo === 'cancelacion';
}

function previewIdDe(doc: DocAudit): string | null {
  const id = doc.data.previewId;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/**
 * Qué confirmaciones aparecen en los documentos que tenemos pero **sin su `intent`**: son las que hay
 * que ir a buscar más atrás.
 *
 * Se mira el `intent` y no el `outcome` porque el `outcome` puede no existir todavía —una
 * confirmación en curso no lo tiene, y es justo el caso que rompía la pantalla—, mientras que el
 * `intent` existe siempre desde el primer instante: se escribe antes de encolar nada.
 *
 * `yaBuscados` son las que ya se buscaron sin éxito. No se vuelven a buscar: cada `progress` nuevo
 * empuja el `intent` MÁS atrás, así que lo que no se alcanzó hoy no se va a alcanzar luego, y
 * reintentarlo en cada polling convertiría el arreglo en una tormenta de lecturas.
 */
export function previewIdsSinIntent(
  docs: readonly DocAudit[],
  yaBuscados: ReadonlySet<string> = new Set(),
): string[] {
  const vistos = new Set<string>();
  const conIntent = new Set<string>();

  for (const doc of docs) {
    const previewId = previewIdDe(doc);
    if (previewId === null) continue;
    vistos.add(previewId);
    if (doc.data.tipo === 'intent') conIntent.add(previewId);
  }

  return [...vistos].filter((id) => !conIntent.has(id) && !yaBuscados.has(id));
}

/**
 * Une documentos por id, quedándose con la primera aparición.
 *
 * Los duplicados son normales y esperados: la cabecera cacheada de una confirmación sigue llegando en
 * la primera página mientras sea reciente. Deduplicar por id es seguro porque `audit_log` es
 * insert-only (invariante 6): un documento con el mismo id es el mismo evento, no una versión nueva.
 */
export function unirDocs(...grupos: readonly DocAudit[][]): DocAudit[] {
  const porId = new Map<string, DocAudit>();
  for (const grupo of grupos) {
    for (const doc of grupo) {
      if (!porId.has(doc.id)) porId.set(doc.id, doc);
    }
  }
  return [...porId.values()];
}
